/**
 * Floor — Phase 2. Boundary is a CLOSED path2d drawn once; all subsequent editing is
 * handles-only (no modal sketch mode). Assembly-driven thickness extends DOWN from the
 * level (Revit convention: top of structure at level + offset).
 *
 * FLOOR FRAMING round: a wood-core floor derives joists automatically (the
 * wall-framing round's default-on model — no instance toggle; joist DEPTH derives
 * from the assembly's structural core, same as a wall stud). `joistDirectionDeg` is
 * the one real geometric choice — which way the joists run.
 */
import type { CategorySchema } from '../../registry'
import { TEXTURE_PLACEMENT_GROUP } from '../../texturePlacement'
import { INSULATION_PARAM_GROUP } from '../../insulationSpec'
import { JOIST_SPACINGS } from '../../floorFraming'
import { FLOOR_CORE_TYPES, TJI_JOIST_SIZES, WOOD_JOIST_SIZES } from '../../floorCore'

export const floorSchema: CategorySchema = {
  id: 'floor',
  pickRank: 2,
  pathParamKey: 'boundary',
  label: 'Floor',
  usesAssembly: 'floor',
  typeParams: {
    groups: [
      {
        // FLOOR CORE DEFINITION round (2026-08-18, Matt — the wall pattern): the type
        // declares its core; conform-on-write derives the core LAYER's material and,
        // for the joist families, its thickness from the selected size (actual
        // depths). Concrete keeps a manual slab thickness AND a free material choice
        // (Slab vs Paving are both legitimate — floorCore.ts).
        label: 'Construction',
        params: [
          {
            key: 'coreType', label: 'Core', kind: 'choice', default: 'wood',
            choices: FLOOR_CORE_TYPES.map((t) => ({ value: t.value, label: t.label })),
          },
          {
            key: 'woodJoistSize', label: 'Joist Size', kind: 'choice', default: '2x10',
            conditionalOn: { param: 'coreType', equals: 'wood' },
            choices: WOOD_JOIST_SIZES.map((s) => ({ value: s.value, label: `${s.label} — ${s.actualLabel} actual` })),
          },
          {
            key: 'tjiJoistSize', label: 'Joist Size', kind: 'choice', default: '11-7/8',
            conditionalOn: { param: 'coreType', equals: 'tji' },
            choices: TJI_JOIST_SIZES.map((s) => ({ value: s.value, label: s.label })),
          },
        ],
      },
      // INSULATION SCHEDULE round (2026-10-03): the type's insulation spec — feeds the
      // derived, fit-checked insulation schedule on the General Notes sheet (insulation.ts)
      INSULATION_PARAM_GROUP,
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Constraints',
        params: [
          { key: 'boundary', label: 'Boundary', kind: 'path2d', default: null },
          // PHASE SPLIT round: opaque, never palette-rendered/transferred — same
          // treatment as topo's points/breaklines/modifiers (types.ts#PhaseSplitEntry).
          { key: 'phaseSplits', label: 'Phase Splits', kind: 'data', default: null, hidden: true },
          // PHASE AREAS (2026-10-30): closed areas sketched in the outline editor (types.ts#PhaseAreaEntry)
          { key: 'phaseAreas', label: 'Phase Areas', kind: 'data', default: null, hidden: true },
          // PAINTED FLOOR FINISHES (2026-09-28): the transition lines and the
          // painted areas between them (Paint Bucket) — see commands/slabFinish.ts
          { key: 'finishSplits', label: 'Finish Transitions', kind: 'data', default: null, hidden: true },
          { key: 'finishAreas', label: 'Finish Areas', kind: 'data', default: null, hidden: true },
          // OUTLINE EDITOR (2026-09-28, Matt: "yes, allow holes"): inner closed loops
          // drawn in Edit Boundary — openings through the slab (Path2D[])
          { key: 'boundaryHoles', label: 'Openings', kind: 'data', default: null, hidden: true },
          // FRAMING ZONES (2026-10-07): framing lines drawn in Edit Boundary, each with
          // its seam beam, and the per-zone joist direction/spacing (types.ts)
          { key: 'framingSplits', label: 'Framing Lines', kind: 'data', default: null, hidden: true },
          { key: 'framingZones', label: 'Framing Zones', kind: 'data', default: null, hidden: true },
          { key: 'heightOffsetFt', label: 'Height Offset From Level', kind: 'length', default: 0, min: -10, max: 10 },
        ],
      },
      {
        // Both params gate on a JOIST core (cross-schema conditionalOn) — a concrete
        // slab has no module or direction to set.
        label: 'Framing',
        params: [
          {
            // ASSEMBLY INSULATION (2026-10-14, Matt: "floor, roof, and ceilings to have
            // insulation as instance parameters like walls do"): batt-hatches the CORE band
            // in section (section.ts's assembly band pass), framing members masking it.
            // No 3D effect — the wall `insulation` toggle's exact contract.
            key: 'insulation', label: 'Insulation', kind: 'bool', default: false,
            conditionalOn: { param: 'coreType', equals: ['wood', 'tji', 'metal'] },
          },
          {
            // INSTANCE, not type (FLOOR CORE DEFINITION round — the wall
            // studSpacingIn precedent): every floor of a given type doesn't
            // necessarily want the same joist module.
            key: 'joistSpacingIn', label: 'Joist Spacing', kind: 'choice', default: '16',
            conditionalOn: { param: 'coreType', equals: ['wood', 'tji'] },
            choices: JOIST_SPACINGS.map((s) => ({ value: String(s), label: `${s}″ o.c.` })),
          },
          {
            // 0 = joists run along world x. Positional: it's a relationship to this
            // floor's own plan (the column/object rotationDeg precedent), never a
            // style to carry over via eyedropper/inject.
            key: 'joistDirectionDeg', label: 'Joist Direction', kind: 'number', default: 0,
            min: -360, max: 360, positional: true,
            conditionalOn: { param: 'coreType', equals: ['wood', 'tji'] },
          },
        ],
      },
      // ATMOSPHERE VIEW Stage 3: per-element texture shift/rotate (texturePlacement.ts)
      TEXTURE_PLACEMENT_GROUP,
    ],
  },
}
